/** Audit trail: every create, edit, delete, upload and AI run. */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi, qs, fetchAllRows } from '../lib/api'
import { Picker } from '../components/Select'
import { DateRangeField, DatePresets } from '../components/DateField'
import {
  Card, Field, Badge, Empty, Loading, ErrorState, Modal, Filters, FilterActions, countFilters,
  useSort, useToast, fmtInt, fmtDateTime, downloadCsv,
} from '../components/ui'
import { PagedTable } from '../components/DataTable'

const ACTION_TONE = {
  create: 'ok',
  update: 'info',
  delete: 'danger',
  upload: 'accent',
  import: 'accent',
  ai: 'warn',
  login: undefined,
}

const ENTITY_LINK = {
  newsletter: (id) => `/newsletters/${id}`,
  partner: (id) => `/partners/${id}`,
  prompt: () => '/ai/prompts',
  user: () => '/settings/users',
  media: () => '/media',
}

const EMPTY = { action: null, entity: null, user: null, from: '', to: '' }

/**
 * An edit records what changed, as [before, after]. An AI run or an upload records
 * what was involved — which tool or model read the file, what came out of it — and
 * has no before to compare against, so it is not offered or explained as a diff.
 */
const isDiff = (row) => row.action === 'create' || row.action === 'update'

export default function AuditLog() {
  const [f, setF] = useState(EMPTY)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [detail, setDetail] = useState(null)
  const toast = useToast()
  const { sort, dir, onSort: sortBy } = useSort('created_at', 'desc', ['created_at'])
  const onSort = (key) => {
    sortBy(key)
    setPage(1)
  }

  const set = (patch) => {
    setF((prev) => ({ ...prev, ...patch }))
    setPage(1)
  }

  const query = useApi(['audit', f, page, limit, sort, dir], `audit${qs({ ...f, page, limit, sort, dir })}`, {
    keepPreviousData: true,
  })

  // Everything these filters match, in this order — not the page on screen.
  const exportAll = async () => {
    try {
      const all = await fetchAllRows('audit', { ...f, sort, dir })
      downloadCsv('audit-log.csv', all.rows, [
        { key: 'created_at', label: 'When' },
        { key: 'user_email', label: 'User' },
        { key: 'action', label: 'Action' },
        { key: 'entity', label: 'Entity' },
        { key: 'entity_id', label: 'Entity id' },
        { key: 'summary', label: 'Summary' },
      ])
      if (all.total > all.rows.length) toast.info(`Exported the first ${fmtInt(all.rows.length)} of ${fmtInt(all.total)} rows`)
    } catch (err) {
      toast.error(err)
    }
  }

  const facet = (kind) =>
    (query.data?.facets ?? [])
      .filter((x) => x.facet === kind && x.value)
      .map((x) => ({ value: x.value, label: `${x.value} (${x.n})` }))

  const columns = [
    { key: 'created_at', label: 'When', sortKey: 'created_at', width: 170, cellClass: 'nowrap small', render: (r) => fmtDateTime(r.created_at) },
    { key: 'user_email', label: 'User', sortKey: 'user_email', width: 190, cellClass: 'small mono', render: (r) => r.user_email ?? '—' },
    { key: 'action', label: 'Action', sortKey: 'action', width: 100, render: (r) => <Badge tone={ACTION_TONE[r.action]}>{r.action}</Badge> },
    {
      key: 'entity',
      label: 'Entity',
      sortKey: 'entity',
      width: 120,
      cellClass: 'small',
      render: (r) => {
        const link = ENTITY_LINK[r.entity]?.(r.entity_id)
        return link && r.entity_id ? (
          <Link to={link}>
            {r.entity} #{r.entity_id}
          </Link>
        ) : (
          <span>
            {r.entity}
            {r.entity_id ? ` #${r.entity_id}` : ''}
          </span>
        )
      },
    },
    { key: 'summary', label: 'Summary', cellClass: 'small muted', render: (r) => r.summary ?? '—' },
    {
      key: 'detail',
      label: '',
      width: 70,
      render: (r) =>
        r.diff_json && (
          <button className="btn btn-sm" onClick={() => setDetail(r)}>
            {isDiff(r) ? 'Diff' : 'Detail'}
          </button>
        ),
    },
  ]

  return (
    <PageHeader
      title="Audit Log"
      sub={query.data ? `${fmtInt(query.data.total)} recorded actions` : 'Loading…'}
      actions={
        <button className="btn btn-sm" onClick={exportAll} disabled={!query.data?.total}>
          ↓ CSV
        </button>
      }
    >
      <div className="col">
        <Filters id="audit" active={countFilters(f)} onClear={() => set(EMPTY)}>
          <Field label="Action" htmlFor="a-action">
            <Picker inputId="a-action" value={f.action} onChange={(v) => set({ action: v })} options={facet('action')} placeholder="Any" />
          </Field>
          <Field label="Entity" htmlFor="a-entity">
            <Picker inputId="a-entity" value={f.entity} onChange={(v) => set({ entity: v })} options={facet('entity')} placeholder="Any" />
          </Field>
          <Field label="User" htmlFor="a-user">
            <Picker inputId="a-user" value={f.user} onChange={(v) => set({ user: v })} options={facet('user')} placeholder="Any" />
          </Field>
          <Field label="Date range">
            <DateRangeField from={f.from} to={f.to} onFrom={(v) => set({ from: v })} onTo={(v) => set({ to: v })} />
          </Field>
          <FilterActions active={countFilters(f)} onClear={() => set(EMPTY)} busy={query.isFetching}>
            <DatePresets onPick={(from, to) => set({ from: from ?? '', to: to ?? '' })} />
          </FilterActions>
        </Filters>

        {query.isLoading && (
          <Card>
            <Loading />
          </Card>
        )}
        {query.isError && (
          <Card>
            <ErrorState error={query.error} retry={query.refetch} />
          </Card>
        )}

        {query.data && !query.data.rows.length && (
          <Card>
            <Empty icon="logs" title="Nothing recorded yet">
              Edits, deletions, uploads and AI runs all land here as they happen.
            </Empty>
          </Card>
        )}

        {query.data?.rows.length ? (
          <PagedTable
            compact
            data={query.data}
            columns={columns}
            sort={sort}
            dir={dir}
            onSort={onSort}
            onPage={setPage}
            onLimit={(n) => {
              setLimit(n)
              setPage(1)
            }}
          />
        ) : null}
      </div>

      <Modal
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail && !isDiff(detail) ? 'What was recorded' : 'Recorded change'}
        size="lg"
      >
        {detail && (
          <div className="col">
            <dl className="kv">
              <dt>When</dt>
              <dd>{fmtDateTime(detail.created_at)}</dd>
              <dt>User</dt>
              <dd className="mono small">{detail.user_email}</dd>
              <dt>Action</dt>
              <dd>{detail.action}</dd>
              <dt>Entity</dt>
              <dd>
                {detail.entity} #{detail.entity_id}
              </dd>
            </dl>
            <p className="small muted">{detail.summary}</p>
            <div
              className="mono small"
              style={{
                padding: 12,
                background: 'var(--bg-sunken)',
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                overflowX: 'auto',
              }}
            >
              {(() => {
                try {
                  return JSON.stringify(JSON.parse(detail.diff_json), null, 2)
                } catch {
                  return detail.diff_json
                }
              })()}
            </div>
            <p className="tiny subtle">
              {isDiff(detail) ? (
                <>
                  Values are shown as <code>[before, after]</code>. Update bodies are recorded as
                  character counts rather than full text to keep the log small.
                </>
              ) : (
                <>
                  <code>engines</code> lists every step of the read in order, each with the tool or
                  AI model that performed it — <code>kind: &quot;tool&quot;</code> is deterministic
                  code that copied the characters, <code>kind: &quot;model&quot;</code> is an
                  inference that could have got them wrong.
                </>
              )}
            </p>
          </div>
        )}
      </Modal>
    </PageHeader>
  )
}
